import React, {
  useCallback,
  useLayoutEffect,
  useState,
  useEffect,
  useRef,
} from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { differenceInMinutes } from 'date-fns';
import debug from 'debug';
import { useTranslation } from 'react-i18next';
import {
  translateHoursTypeOptions,
  type CatalogEntry,
} from '../../../../utils/catalogLabels';
// INTEGRACIÓN 9: la ubicación sin exigir router — el bloque también se pinta
// en el modal de «Mover tramos», que vive fuera de él.
import { useOptionalLocation } from '../../../../hooks/useOptionalLocation';

// Components
import Tabs from '../../../display/Tabs';
import { OperatorsSelectionTable } from '../../../display/tables/formTables/OperatorsSelectionTable';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { MainButton } from '../../../inputs/buttons/MainButton';
import InputText from '../../../inputs/InputText';
import { DateTime } from '../../../inputs/DateTime';
import { ExternalCompaniesSelectionTable } from '../../../display/tables/formTables/ExternalCompaniesSelectionTable';

// Types
import {
  TimeValue,
  type OperatorsAndVehiclesFormValues,
  type OperatorsAndVehiclesType,
  DriverList,
} from './types';
import {
  OperatorsSelectionType,
  VehiclesSelectionType,
} from '../../../display/tables/formTables/OperatorsSelectionTable/types';
import { ExternalCompaniesSelectionType } from '../../../display/tables/formTables/ExternalCompaniesSelectionTable/types';

// Switch actuación simultánea
import SimpleSwitch from '../../../inputs/buttons/SwitchButton/Simple';

// Styles
import {
  StyledTypography,
  TimeContainer,
  BreakdownContainer,
  SimultaneousContainer,
  SimultaneousHeader,
} from './styles';
import { GeneralContainer, ButtonContainer, ContainerError } from '../styles';

// Utilities
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { deriveCrew } from './crew';
import { compressSessionStorage } from '../../../../utils/sessionStorage'; // ✅ INTEGRACIÓN 9
import { yupResolver } from '@hookform/resolvers/yup';
import {
  createOperatorsAndVehicleSchema,
  isArrivalBeforeEnd,
  isArrivalNotBeforeStart,
  isEndNotBeforeStart,
} from './schema';
import { Typography } from '../../../display/Typography';
import { tabsType } from '../../../../state/reducers/ui/tabs';

// Constants
import { OrderState } from '../../../../types/orders';
import { sameScheduleValue } from '../ActionEdition/basicSchedule';

// ⚠️ SINCRONIZACIÓN: Eventos de vehículos
import { useVehicleEvents } from '../../../../utils/vehicleEvents';

// Feature flags - BUG #20 FIX: Usar useFeatureFlagWithLoading para esperar carga
import { useFeatureFlagWithLoading } from '../../../../hooks/useFeatureFlag';

/** Las tres fechas de «Distribución de horas»: se revalidan juntas. */
const DATE_FIELDS = [
  'registerInitDate',
  'registerArrivalDate',
  'registerEndDate',
] as const;

/** Códigos de `checkSpecialValues` cuyo motivo va bajo la fecha, no en el pie. */
const DATE_ERROR_CODES = [
  'end-before-start',
  'arrival-before-start',
  'arrival-not-before-end',
];

const OperatorsAndVehicleForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
  textButton = 'Continuar',
  hideSubmitButton = false,
  hideExternalsTab = false,
  hideHoursDistribution = false,
  hideSimultaneous = false,
  externalErrors,
  onCrewChange,
  showCrewStateFilter,
}: OperatorsAndVehiclesType) => {
  const { t } = useTranslation('forms');

  // BUG borrador RV (2026-07-12): la caché de conductores era una key GLOBAL
  // ('drivers') compartida por los wizards de actuación, validación y RV —
  // los conductores de un wizard resucitaban como vehículos pre-marcados en
  // otro. Cada wizard tiene ahora su bucket propio, misma convención que los
  // hermanos `_interno`/`_externals` (quien limpia el borrador base limpia
  // también este sufijo).
  const driversCacheKey = `${namingPersistForm}_drivers`;

  // Feature flag para mostrar/ocultar campo "Tipo de horas"
  // BUG #20 FIX: Usar useFeatureFlagWithLoading para esperar a que cargue antes de renderizar
  const { isEnabled: showHoursType, loading: featureFlagLoading } = useFeatureFlagWithLoading('showHoursType', true);

  // Configure debug namespaces
  const logMain = debug('rois:operators:main');
  const logState = debug('rois:operators:state');
  const logTabs = debug('rois:operators:tabs');
  const logForm = debug('rois:operators:form');
  const logInternal = debug('rois:operators:internal');
  const logExternal = debug('rois:operators:external');
  const logSubmit = debug('rois:operators:submit');
  const logVehicleData = debug('rois:operators:vehicle-data');
  const logError = debug('rois:operators:error');
  const logFeatureFlag = debug('rois:operators:feature-flag');

  // Enable logs only in development (user can also enable via localStorage)
  if (import.meta.env.DEV) {
    const current = debug.disable();
    if (!current.includes('rois:operators:*')) {
      debug.enable(`${current ? current + ',' : ''}rois:operators:*`);
    } else {
      debug.enable(current);
    }
    logMain('Debug activado para módulo de operadores y vehículos');
  }

  // BUG #20 DEBUG: Log del estado del feature flag
  logFeatureFlag('Feature flag estado: loading =', featureFlagLoading, ', showHoursType =', showHoursType);
  logFeatureFlag('Schema se creará con hoursType =', showHoursType ? 'REQUIRED' : 'OPTIONAL');

  // INTEGRACIÓN 9: Detectar validación diferida desde Resumen
  const locationState = useOptionalLocation();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    trigger, // ✅ Necesario para validación manual
  } = useForm<OperatorsAndVehiclesFormValues>({
    defaultValues: {
      ...initialData,
      registerInitDate: initialData?.registerInitDate
        ? initialData?.registerInitDate
        : new Date().toISOString(),
      // Sin fecha-fin previa → arranca VACÍO (igual que registerArrivalDate),
      // NO `new Date()`. Defaultar a "ahora" derrotaba el guard
      // `registerEndDate ? registerEndDate : undefined` de saveData → el fin se
      // persistía con el momento de guardado/sync (actuaciones de días → horas
      // infladas en Gestión Horaria). Vacío → el guard deja el fin null ("sin
      // cerrar" → 0h); el operario pone el fin real cuando lo tiene.
      registerEndDate: initialData?.registerEndDate || '',
      // Hora de llegada opcional: arranca vacía (no se autocompleta con "ahora").
      registerArrivalDate: initialData?.registerArrivalDate || '',
      isExternal: initialData.isExternal,
      isSimultaneous: initialData.isSimultaneous ?? false,
    },
    resolver: yupResolver(createOperatorsAndVehicleSchema(showHoursType)) as any,
  });

  // INTEGRACIÓN 9: Ejecutar validación al montar si viene del resumen con errores
  useEffect(() => {
    if (locationState?.state?.validateOnMount) {
      trigger();

      // ✅ FIX: Activar flag para preservar errores mientras validamos
      preserveErrorRef.current = true;

      // ✅ FIX: Ejecutar validación custom y mostrar error visual
      // Usar setTimeout para asegurar que los watchers están actualizados
      setTimeout(() => {
        const checkResults = checkSpecialValues();
        if (!checkResults.isValid && checkResults.errorCode) {
          // Mapear código de error a mensaje visual
          // Los errores de FECHA no van a la franja del pie: `trigger()` de
          // arriba ya los pinta bajo su campo (como «Datos Act»). El chequeo
          // sigue devolviéndolos primero para no sacar un «sin operarios»
          // espurio (ver `checkSpecialValues`).
          if (DATE_ERROR_CODES.includes(checkResults.errorCode)) return;
          const errorMessages: Record<string, string> = {
            'no-operators': t('operatorsAndVehicles.errors.noOperators', 'Debes seleccionar al menos un operario'),
            'incomplete-vehicles': t('operatorsAndVehicles.errors.incompleteVehicles', 'Debes asignar conductor a todos los vehículos'),
            'invalid-hours': t('operatorsAndVehicles.errors.invalidHours', 'Las horas no son válidas'),
            'no-external-operators': t('operatorsAndVehicles.errors.noExternalOperators', 'Debes indicar operarios para las empresas externas'),
          };
          const message = errorMessages[checkResults.errorCode] || t('operatorsAndVehicles.errors.generic', 'Hay errores en el formulario');
          setOperatorError(message);
        }
        // ✅ FIX: Desactivar flag después de un tiempo para permitir limpiar errores
        // cuando el usuario corrija los problemas
        setTimeout(() => {
          preserveErrorRef.current = false;
        }, 500);
      }, 100);
    }
  }, []);

  // INTEGRACIÓN 9: Función para guardar draft y navegar SIN validar
  const handleSaveDraft = () => {
    // Calcular isExternal basándose en datos reales antes de guardar
    const externals = getValues('externals') || [];
    const hasExternalOps = externals.reduce(
      (acc: number, ext: { operatorsNumber?: number | string }) =>
        acc + Number(ext.operatorsNumber || 0), 0
    ) > 0;
    setValue('isExternal', hasExternalOps);

    const values = getValues();

    // Bug #13 — Persistir SIEMPRE a sessionStorage antes de cualquier otra
    // cosa. El validador final (`formValidation.ts`) lee `operatorsTable`
    // desde sessionStorage; si no persistimos aquí (p.ej. con un return
    // temprano por error de fecha) se pierde la selección de operarios y el
    // modal final muestra "Debes seleccionar al menos un operario" espurio
    // aunque el checkbox siga marcado. La validación de fecha NO bloquea
    // este draft-save: vive en el yup schema (modal final) y en
    // `checkSpecialValues` (al volver a la pestaña desde el modal).
    const compressed = compressSessionStorage(JSON.stringify(values));
    sessionStorage.setItem(namingPersistForm, compressed);

    // Llamar al submit handler del padre (que solo navega)
    onFormSubmit(values);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 DEBUG - initialData recibido del loader (usando librería debug)
  // ═══════════════════════════════════════════════════════════════════════════

  logMain('═══════════════════════════════════════════════════════════════════');
  logMain('🎯 OperatorsAndVehicleForm - COMPONENTE MONTADO');
  logMain('═══════════════════════════════════════════════════════════════════');
  logMain('📥 initialData COMPLETO recibido:');
  logState('  flowData:', initialData?.flowData);
  logState('  isExternal:', initialData?.isExternal);
  logState('  hoursType:', initialData?.hoursType);
  logState('  registerInitDate:', initialData?.registerInitDate);
  logState('  registerEndDate:', initialData?.registerEndDate);
  logState('  totalHours:', initialData?.totalHours);

  logInternal('═══ OPERARIOS INTERNOS ═══');
  logInternal('  operatorsTable length:', initialData?.operatorsTable?.length || 0);
  if (initialData?.operatorsTable?.length) {
    initialData.operatorsTable.forEach((op, idx) => {
      if (op) {
        logInternal(`    [${idx}] ${op.name} ${op.surName} (${op.id}) - internal: ${(op as any).internal}`);
      } else {
        logInternal(`    [${idx}] ⚠️ VALOR NULL/UNDEFINED`);
      }
    });
  } else {
    logInternal('  ⚠️ NO HAY OPERARIOS INTERNOS EN initialData');
  }

  logExternal('═══ TRABAJOS EXTERNOS (externals) ═══');

  logVehicleData('═══ VEHÍCULOS ═══');
  logVehicleData('  vehicleTable keys:', Object.keys(initialData?.vehicleTable || {}).length);
  if (initialData?.vehicleTable) {
    Object.entries(initialData.vehicleTable).forEach(([key, value]: [string, any]) => {
      if (value) {
        logVehicleData(`    [${key}] driverID: ${value?.driverID}, vehicleOccupantID: ${value?.vehicleOccupantID}`);
      } else {
        logVehicleData(`    [${key}] ⚠️ VALOR NULL/UNDEFINED`);
      }
    });
  } else {
    logVehicleData('  ⚠️ vehicleTable es null/undefined');
  }

  logExternal('═══ TRABAJOS EXTERNOS ═══');
  logExternal('  externals length:', initialData?.externals?.length || 0);

  // Detectar posibles problemas
  if (initialData?.operatorsTable?.length === 0 && initialData?.flowData === 'validation') {
    logError('🚨 BUG POTENCIAL: flowData=validation pero operatorsTable está vacío');
    logError('   Verificar que el servidor devuelve operarios y que el smart merge funciona');
  }
  logMain('═══════════════════════════════════════════════════════════════════');

  useCacheFormInSessionStorage(namingPersistForm, control);

  // Tour P5 (2026-07-07): notifica al padre cada cambio de tripulación para
  // que pueda recalcular sus validaciones EN VIVO — el wizard RV limpia así
  // las frases rojas de FR-3.4 según se corrigen. La derivación vive en
  // deriveCrew (crew.ts), la MISMA que usa el Iniciar del wizard sobre el
  // borrador persistido. Opcional: sin `onCrewChange` no hace nada.
  const crewWatch = useWatch({ control });
  useEffect(() => {
    if (!onCrewChange) return;
    onCrewChange(deriveCrew(crewWatch as any));
  }, [crewWatch, onCrewChange]);

  // States
  const [allOperators, setAllOperators] = useState<OperatorsSelectionType[]>(
    []
  );

  const [operatorError, setOperatorError] = useState<string | null>(null);
  // ✅ FIX: Ref para preservar errores durante validateOnMount
  const preserveErrorRef = useRef(false);
  const [timeValue, setTimeValue] = useState<TimeValue>({
    hour: '0',
    minutes: '0',
  });

  const defaultSelectedTab = initialData.isExternal
    ? initialData.isExternal === true
      ? 1
      : 0
    : 0;

  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab ?? 0);
  const [prevSelectedTab, setPrevSelectedTab] = useState<number | undefined>(
    undefined
  );

  logTabs(
    `🔄 Tab inicial seleccionada: ${defaultSelectedTab} (0=Internos, 1=Externos)`
  );

  // Table defaultData
  const operatorsDefaultData = initialData?.operatorsTable
    ? initialData.operatorsTable.filter((operator) => operator)
    : [];
  const vehiclesInitialData = initialData?.vehicleTable
    ? Object.values(initialData.vehicleTable).filter((operator) => operator)
    : [];

  const [defaultOperators, setDefaultOperators] = useState<
    OperatorsSelectionType[]
  >(operatorsDefaultData);

  const [defaultExternals, setDefaultExternals] = useState<
    ExternalCompaniesSelectionType[]
  >(initialData?.externals ?? []);

  const defaultVehiclesObject = initialData.vehicleTable;

  // Un vehículo cuenta como SELECCIONADO solo si su entrada identifica al
  // vehículo (`vehicleID`/`id`) — el mismo criterio que usa la rehidratación
  // del borrador en `OperarisTabContent` y que `deriveCrew`.
  //
  // Con `Object.keys()` a secas contaba también las entradas a medias, que las
  // hay: el desplegable «Conductor» se pinta en TODAS las filas y escribir un
  // conductor en una fila NO marcada dejaba `{ driverID }` suelto. En el
  // siguiente montaje ese resto entraba aquí como vehículo preseleccionado y
  // el efecto de re-auto-asignación le terminaba de escribir el `vehicleID`:
  // un vehículo que nadie marcó acababa en el recorrido (incidente
  // 2026-08-08). El desplegable ya no deja hacerlo, pero los borradores
  // envenenados que aún anden por ahí siguen entrando por aquí.
  const selectedVehiclesIds = defaultVehiclesObject
    ? Object.entries(defaultVehiclesObject)
        .filter(([, v]) => (v as any)?.vehicleID || (v as any)?.id)
        .map(([key]) => key)
    : [];

  const filterDefaultVehicles = initialData.internalOperators.vehicles.filter(
    (vehicle) => selectedVehiclesIds.includes(vehicle.id)
  );

  const [defaultVehiclesSelected, setDefaultVehiclesSelected] = useState<
    VehiclesSelectionType[]
  >(filterDefaultVehicles);

  const [defaultHoursData, setDefaultHoursData] = useState<{
    registerInitDate?: string;
    registerEndDate?: string;
    registerArrivalDate?: string;
    hoursType?: string;
    totalHours?: string;
  }>({
    registerInitDate: initialData?.registerInitDate || new Date().toISOString(),
    registerEndDate: initialData?.registerEndDate,
    registerArrivalDate: initialData?.registerArrivalDate,
    hoursType: initialData?.hoursType,
    totalHours: initialData?.totalHours,
  });

  // ⚠️ SINCRONIZACIÓN: Estado dinámico de vehículos disponibles
  const initVehicles = initialData?.internalOperators?.vehicles || [];
  // `setAvailableVehicles` no lo llama nadie (el catálogo de vehículos llega
  // por props y no cambia en caliente); se conserva el estado por si el día
  // que se cablee `useVehicleEvents` aquí haga falta, pero OJO: la tabla hija
  // ya no monta un hook por vehículo, así que cambiar su longitud es seguro.
  const [availableVehicles] = useState<VehiclesSelectionType[]>(initVehicles);

  // Watchers
  const startTimeWatcher = useWatch({
    control,
    name: 'registerInitDate',
  });

  const finishTimeWatcher = useWatch({
    control,
    name: 'registerEndDate',
  });

  const arrivalTimeWatcher = useWatch({
    control,
    name: 'registerArrivalDate',
  });

  // Errores de fecha EN EL CAMPO (3-nov), como «Datos Act» de la básica
  // (`onBasicScheduleChange`): al cambiar una fecha se revalidan las tres, así
  // el motivo sale bajo la fecha en cuanto se pone y se quita en cuanto se
  // corrige — también al volver del aviso de errores, donde `trigger()` sin
  // submit no revalida solo. Solo con un cambio de verdad: `DateTime` avisa
  // también al montarse con la misma hora, y eso no debe pintar errores.
  const onDateChange =
    (
      onChange: (value: string | undefined) => void,
      current: unknown,
      emptyAs?: string
    ) =>
    (date?: string) => {
      const next = date ?? emptyAs;
      onChange(next);
      if (!sameScheduleValue(next, current)) {
        void trigger([...DATE_FIELDS]);
      }
    };

  // Al tocar la llegada se quita la queja roja, igual que pasa con inicio/final
  // en el efecto de abajo (que no escucha la llegada: no cambia las horas).
  useEffect(() => {
    if (!preserveErrorRef.current) {
      setOperatorError('');
    }
  }, [arrivalTimeWatcher]);

  const operatorsTableWatcher = useWatch({
    control,
    name: 'operatorsTable',
  });

  const internalHoursType = useWatch({
    control,
    name: 'hoursType',
  });

  const externsDataWatcher = useWatch({
    control,
    name: 'externals',
  });

  // Actuación simultánea: cuando está activa, las horas NO suman al total.
  const isSimultaneousWatcher = useWatch({
    control,
    name: 'isSimultaneous',
  });

  const vehiclesTableWatcher =
    useWatch({
      control,
      name: 'vehiclesTable',
    }) ?? [];

  useLayoutEffect(() => {
    if (operatorsTableWatcher || externsDataWatcher) {
      logState('🔄 Actualizando estado por cambios en operadores o externos');

      const validOperators: OperatorsSelectionType[] = [
        ...(operatorsTableWatcher || []),
      ].filter((operator) => operator);
      const externsOperators = externsDataWatcher?.reduce(
        (accumulator, currentVal) =>
          accumulator + Number(currentVal.operatorsNumber),
        0
      );
      // Total combinado: internos + externos
      const allOpsCount = validOperators.length + (externsOperators || 0);
      setAllOperators(validOperators);

      logState(`📊 Operadores internos: ${validOperators.length}`);
      logState(`📊 Operadores externos: ${externsOperators || 0}`);
      logState(`📊 Total combinado: ${allOpsCount}`);

      const totalMinutes = calculateHourDifference(
        finishTimeWatcher,
        startTimeWatcher
      );

      const totalHours = Math.floor((totalMinutes * allOpsCount) / 60)
        .toString()
        .padStart(2, '0');
      const minutesDifference = ((totalMinutes * allOpsCount) % 60)
        .toString()
        .padStart(2, '0');

      setTimeValue({ hour: totalHours, minutes: minutesDifference });
      setValue('totalHours', `${totalHours}:${minutesDifference}`);
      // ✅ FIX: No limpiar error si estamos en proceso de validateOnMount
      if (!preserveErrorRef.current) {
        setOperatorError('');
      }
      setDefaultHoursData({
        registerInitDate: startTimeWatcher,
        registerEndDate: finishTimeWatcher,
        hoursType: internalHoursType,
        totalHours: totalHours,
      });

    }
  }, [
    startTimeWatcher,
    finishTimeWatcher,
    operatorsTableWatcher,
    internalHoursType,
    externsDataWatcher,
    selectedTab,
  ]);

  // Nuevo efecto específico para manejar cambios de pestaña
  useEffect(() => {
    // Solo ejecutar cuando cambie la pestaña y no sea la primera carga
    if (prevSelectedTab !== undefined && prevSelectedTab !== selectedTab) {
      logTabs(`🔄 Cambio de pestaña: ${prevSelectedTab} => ${selectedTab}`);

      // Guardar datos de la pestaña anterior
      if (prevSelectedTab === 0) {
        // Guardar datos de Interno (pestaña anterior)
        const internoData = {
          operatorsTable: getValues('operatorsTable') || [],
          vehiclesTable: getValues('vehiclesTable') || [],
        };

        logInternal('💾 Guardando datos de pestaña Interno');
        logInternal(
          `📊 Operadores internos: ${internoData.operatorsTable?.length || 0
          }`
        );
        logInternal(
          `📊 Vehículos: ${Object.keys(internoData.vehiclesTable || {}).length}`
        );

        // Log each vehicle with more detail to debug issues
        if (internoData.vehiclesTable?.length > 0) {
          logVehicleData(
            '🚗 Detalle de vehículos al cambiar a pestaña Externos:'
          );
          internoData.vehiclesTable.forEach((vehicle, idx) => {
            logVehicleData(
              `  [${idx}] ID: ${vehicle.id}, Driver: ${vehicle.driver}, Plate: ${vehicle.plate}`
            );
          });
        }

        // Guardar en una clave específica para esta pestaña
        sessionStorage.setItem(
          `${namingPersistForm}_interno`,
          JSON.stringify(internoData)
        );
      } else {
        // Guardar datos de Externos (pestaña anterior)
        const externalsData = {
          externals: getValues('externals') || [],
        };

        logExternal('💾 Guardando datos de pestaña Externos');
        logExternal(
          `📊 Empresas externas: ${externalsData.externals?.length || 0}`
        );

        sessionStorage.setItem(
          `${namingPersistForm}_externals`,
          JSON.stringify(externalsData)
        );
      }

      // Cargar datos de la nueva pestaña
      if (selectedTab === 0) {
        // Cargar datos de Interno (pestaña actual)
        logTabs('🔍 Cargando datos de pestaña Interno');
        const internoDataStr = sessionStorage.getItem(
          `${namingPersistForm}_interno`
        );
        if (internoDataStr) {
          const internoData = JSON.parse(internoDataStr);
          logInternal(
            `📥 Cargando ${internoData.operatorsTable?.length || 0
            } operadores internos`
          );
          logInternal(
            `📥 Cargando ${Object.keys(internoData.vehiclesTable || {}).length
            } vehículos`
          );

          // Log each vehicle with more detail to debug issues
          if (internoData.vehiclesTable?.length > 0) {
            logVehicleData(
              '🚗 Detalle de vehículos al cargar pestaña Interno:'
            );
            internoData.vehiclesTable.forEach(
              (vehicle: VehiclesSelectionType, idx: number) => {
                logVehicleData(
                  `  [${idx}] ID: ${vehicle.id}, Driver: ${vehicle.driver}, Plate: ${vehicle.plate}`
                );
              }
            );
          }

          setValue(
            'operatorsTable',
            internoData.operatorsTable
          );
          setValue('vehiclesTable', internoData.vehiclesTable);
        } else {
          logInternal('⚠️ No hay datos guardados para la pestaña Interno');
        }
      } else {
        // Cargar datos de Externos (pestaña actual)
        logTabs('🔍 Cargando datos de pestaña Externos');
        const externalsDataStr = sessionStorage.getItem(
          `${namingPersistForm}_externals`
        );
        if (externalsDataStr) {
          const externalsData = JSON.parse(externalsDataStr);
          logExternal(
            `📥 Cargando ${externalsData.externals?.length || 0
            } empresas externas`
          );

          setValue('externals', externalsData.externals);
        } else {
          logExternal('⚠️ No hay datos guardados para la pestaña Externos');
        }
      }

      // isExternal ya no se muta al cambiar de tab — se calcula al guardar
      // basándose en si hay datos en externals
    }

    // Guardar la pestaña actual para la próxima vez
    setPrevSelectedTab(selectedTab);
  }, [selectedTab, namingPersistForm]);

  const setCacheDrivers = (drivers: DriverList) => {
    const cacheDrivers = JSON.stringify(drivers);
    sessionStorage.setItem(driversCacheKey, cacheDrivers);
  };

  // Tour P5 (2026-07-07): la caché 'drivers' solo manda si tiene entradas —
  // un "[]" persistido ganaba (?? solo salta con null) a los conductores
  // rehidratados de initialData.vehicleTable y el dropdown Conductor se
  // quedaba en "Seleccionar operario" aunque el conductor estuviera asignado.
  const cachedDrivers: DriverList | null = JSON.parse(
    sessionStorage.getItem(driversCacheKey) as string,
  );
  // BUG borrador RV (2026-07-12), defensa en profundidad: aunque la caché ya
  // es por-wizard, solo valen los conductores de vehículos presentes en la
  // selección actual (keys de vehicleTable) — cubre borradores desfasados.
  const relevantCachedDrivers: DriverList = (cachedDrivers || []).filter(
    (driver) => selectedVehiclesIds.includes(String(driver?.vehicleID)),
  );
  const driversFromInitialData: DriverList = vehiclesInitialData
    .filter((v: any) => (v?.vehicleID || v?.id) && (v?.driverID || v?.driver))
    .map((v: any) => ({
      vehicleID: String(v.vehicleID || v.id),
      driverID: String(v.driverID || v.driver),
    }));
  const drivers: DriverList =
    relevantCachedDrivers.length > 0
      ? relevantCachedDrivers
      : driversFromInitialData;

  const dataTabs = [
    {
      label: t('operatorsAndVehicles.interno'),
      content: (
        <>
          <OperatorsSelectionTable
            data={initialData}
            driversCacheKey={driversCacheKey}
            operatorsData={initialData?.internalOperators.operators}
            vehiclesData={availableVehicles}
            titles={[
              t('operatorsAndVehicles.operatorsSelection'),
              t('operatorsAndVehicles.vehiclesSelection'),
            ]}
            control={control}
            defaultOperators={defaultOperators}
            defaultVehicles={defaultVehiclesSelected}
            defaultDrivers={drivers}
            tableName="operatorsTable"
            setDefaultOperators={setDefaultOperators}
            setDefaultVehicles={setDefaultVehiclesSelected}
            setValue={setValue}
            setCacheDrivers={setCacheDrivers}
            isInternal={true}
            getValues={getValues}
            concessions={initialData?.concessions || []}
            teams={initialData?.teams || []}
            showCrewStateFilter={showCrewStateFilter}
          />
        </>
      ),
    },
    {
      label: t('operatorsAndVehicles.externals'),
      content: (
        <ExternalCompaniesSelectionTable
          setValue={setValue}
          getValues={getValues}
          defaultValues={defaultExternals}
          externalCompanies={initialData.externalCompanies}
          setDefaultExternals={setDefaultExternals}
        />
      ),
    },
  ];

  const calculateHourDifference = useCallback(
    (startISODate: string, endISODate: string) => {
      const startTime = new Date(startISODate);
      const endTime = new Date(endISODate);

      // Bug "-1 min": el selector de hora es HH:MM (sin segundos), pero
      // los Date parseados pueden traer segundos/ms residuales (p.ej.
      // 17:31:00.500). `differenceInMinutes` trunca hacia cero por
      // defecto → 360 min reales se mostraban como 359. Normalizamos
      // segundos+ms a 0 en ambos extremos para contar solo HH:MM, que es
      // lo que el usuario eligió. 17:31 → 23:31 = 360 exactos.
      startTime.setSeconds(0, 0);
      endTime.setSeconds(0, 0);

      const minutesDifference = differenceInMinutes(startTime, endTime);

      // Bug #13 — antes hacíamos Math.abs y mostrábamos un total positivo
      // engañoso (ej. "51:12" con dates 14/05 → 12/05 cuando la diferencia
      // real es -2 días). Eso enmascara el error: el usuario ve un número
      // que parece correcto y no entiende por qué el form le bloquea.
      // Mejor devolver 0 → Total H.P. = "00:00" coherente con el error de
      // validación que muestra `checkSpecialValues`. Cero también es seguro
      // para BD si por algún camino offline el valor llega a `minutesTotals`.
      if (isNaN(minutesDifference) || minutesDifference < 0) {
        return 0;
      }

      return minutesDifference;
    },
    [startTimeWatcher, finishTimeWatcher]
  );

  const checkSpecialValues = () => {
    logSubmit('🔍 Comprobando valores especiales para validación');

    // Bug #13 — la fecha final no puede ser anterior a la de inicio.
    // Va PRIMERO a propósito: al volver del modal final a esta pestaña,
    // `allOperators` (state local) puede estar aún vacío por timing del
    // re-mount aunque sessionStorage SÍ tenga el operario marcado. Si
    // dejásemos el check de operarios antes, devolvería un 'no-operators'
    // espurio y el usuario vería "Debes seleccionar al menos un operario"
    // cuando el problema real es la fecha. Los watchers de RHF
    // (start/finishTimeWatcher) sí se hidratan desde defaultValues al
    // mount, así que esta comprobación es fiable en ese momento.
    // Al minuto, como el esquema (`isEndNotBeforeStart`).
    if (!isEndNotBeforeStart(finishTimeWatcher, startTimeWatcher)) {
      logSubmit('❌ La fecha final es anterior a la de inicio');
      return { isValid: false, errorCode: 'end-before-start' };
    }

    // La llegada no puede ser anterior al inicio (3-nov) y tiene que ser
    // anterior a la fecha final. Mismo motivo que el check de arriba para ir
    // antes que el de operarios.
    if (!isArrivalNotBeforeStart(arrivalTimeWatcher, startTimeWatcher)) {
      logSubmit('❌ La fecha de llegada es anterior a la de inicio');
      return { isValid: false, errorCode: 'arrival-before-start' };
    }
    if (!isArrivalBeforeEnd(arrivalTimeWatcher, finishTimeWatcher)) {
      logSubmit('❌ La fecha de llegada no es anterior a la final');
      return { isValid: false, errorCode: 'arrival-not-before-end' };
    }

    const hasOperators = allOperators.length > 0;
    const externsOperators = externsDataWatcher?.reduce(
      (accumulator, currentVal) =>
        accumulator + Number(currentVal.operatorsNumber),
      0
    ) ?? 0;
    const hasExternalOps = externsOperators > 0;

    // Al menos un tipo de operario debe existir
    if (!hasOperators && !hasExternalOps) {
      logSubmit('❌ No hay operarios internos ni empresas externas');
      return { isValid: false, errorCode: 'no-operators' };
    }

    // Validar operarios internos si hay datos
    if (hasOperators) {
      const validHours = Number(timeValue.hour) >= 0;

      logVehicleData('🚗 Validando vehículos en checkSpecialValues:');
      const vehiclesWithData = vehiclesTableWatcher?.filter(
        (vehicle) => vehicle.id || vehicle.driver
      );

      logVehicleData(
        `🔢 Total vehículos con algún dato: ${vehiclesWithData?.length || 0}`
      );

      if (vehiclesWithData && vehiclesWithData.length > 0) {
        vehiclesWithData.forEach((vehicle, idx) => {
          logVehicleData(
            `  [${idx}] ID: ${vehicle.id || 'No ID'}, Driver: ${vehicle.driver || 'No driver'
            }, Plate: ${vehicle.plate || 'No plate'}`
          );
        });
      }

      const vehiclesWithAllData = vehiclesTableWatcher
        ?.filter((vehicle) => vehicle.id || vehicle.driver)
        .every((vehicle) => {
          const hasDriver = 'driver' in vehicle && vehicle['driver'];
          const hasId = 'id' in vehicle && vehicle['id'];
          logVehicleData(
            `  Vehículo ${vehicle.plate || 'sin placa'
            }: hasDriver=${hasDriver}, hasId=${hasId}`
          );
          return hasDriver && hasId;
        });

      const internalChecksPass = validHours && vehiclesWithAllData;
      logSubmit(
        `✅ Validación internos: horas=${validHours}, vehículosCompletos=${vehiclesWithAllData}`
      );

      if (!internalChecksPass) {
        const errorCode = !vehiclesWithAllData
          ? 'incomplete-vehicles'
          : 'invalid-hours';
        return { isValid: false, errorCode };
      }
    }

    // Validar empresas externas si hay datos en el tab externo
    if (hasExternalOps) {
      logSubmit(`✅ Validación externos: operadores=${externsOperators}`);
    }

    // Calcular isExternal para el guardado
    setValue('isExternal', hasExternalOps);

    return { isValid: true, errorCode: null };
  };

  const checkRepeatedDrivers = () => {
    const driversSet = new Set();
    let repeatedDrivers = false;
    for (const vehicle of vehiclesTableWatcher) {
      if (vehicle.driver) {
        if (driversSet.has(vehicle.driver)) {
          repeatedDrivers = true;
          break;
        }
        driversSet.add(vehicle.driver);
      }
    }
    return repeatedDrivers;
  };

  useEffect(() => {
    if (operatorsTableWatcher) {
      // Filtrar valores nulos y undefined
      const validOperators = operatorsTableWatcher.filter((op) => op);

      logInternal(
        `🔍 operatorsTableWatcher actualizado - Total: ${operatorsTableWatcher.length}, Válidos: ${validOperators.length}`
      );

      if (validOperators.length > 0) {
        logInternal('📋 Operadores válidos en operatorsTableWatcher:');
        validOperators.forEach((op, idx) => {
          logInternal(`  [${idx}] ${op.name} ${op.surName} (${op.id})`);
        });
      }

      // Detectar duplicados
      const operatorIds = validOperators.map((op) => op.id);
      const uniqueIds = new Set(operatorIds);
      if (uniqueIds.size < operatorIds.length) {
        logError(
          '⚠️ ALERTA: Detectados IDs duplicados en operatorsTableWatcher'
        );

        // Contar ocurrencias de cada ID para identificar los duplicados
        const counts: Record<string, number> = {};
        operatorIds.forEach((id) => {
          counts[id] = (counts[id] || 0) + 1;
        });

        // Mostrar los IDs duplicados
        for (const [id, count] of Object.entries(counts)) {
          if (count > 1) {
            const duplicateOps = validOperators.filter((op) => op.id === id);
            logError(`  ID duplicado: ${id} (${count} veces)`);
            duplicateOps.forEach((op, idx) => {
              logError(`    [${idx}] ${op.name} ${op.surName}`);
            });
          }
        }
      }
    }
  }, [operatorsTableWatcher]);

  // Monitorizar cambios en el formulario completo
  const formValues = useWatch({
    control,
  });

  useEffect(() => {
    if (formValues?.operatorsTable) {
      logForm('📊 Form values actualizado');
      logForm(
        `  operatorsTable: ${formValues.operatorsTable.filter(Boolean).length
        } operadores`
      );
    }
  }, [formValues]);

  // Quitando la función onFormSubmit duplicada y creando una para logging
  useEffect(() => {
    // Registrar los datos cuando se vaya a enviar el formulario
    const logFormData = () => {
      logSubmit('🚀 Preparando datos para enviar:');
      const data = getValues();

      // Información sobre operadores internos
      if (data.operatorsTable) {
        const validOperators = data.operatorsTable.filter(Boolean);
        logSubmit(`📋 Operadores internos (${validOperators.length}):`);
        validOperators.forEach((op, idx) => {
          if (op) {
            logSubmit(`  [${idx}] ${op.name} ${op.surName} (${op.id})`);
          }
        });
      }

      // Información sobre vehículos
      if (data.vehicleTable) {
        const vehiclesKeys = Object.keys(data.vehicleTable);
        logSubmit(`🚗 Vehículos (${vehiclesKeys.length}):`);
        vehiclesKeys.forEach((key) => {
          const vehicle = data.vehicleTable[key];
          if (vehicle && vehicle.vehicleID) {
            logSubmit(
              `  [${key}] ID: ${vehicle.vehicleID}, Driver: ${vehicle.driverID || 'No driver'
              }`
            );
          }
        });
      }

      // Información sobre empresas externas
      if (data.externals) {
        logSubmit(`🏢 Empresas externas (${data.externals.length}):`);
        data.externals.forEach((ext, idx) => {
          logSubmit(
            `  [${idx}] ${ext.company}: ${ext.operatorsNumber} operarios`
          );
        });
      }

      logSubmit(`🔄 isExternal: ${data.isExternal}`);
    };

    // Para debugging, loguear también al cargar el componente
    logFormData();

    // Agregar un event listener para capturar la submission del formulario
    const formElement = document.querySelector('form');
    if (formElement) {
      const submitListener = () => {
        logSubmit('🔔 Evento submit capturado');
        logFormData();
      };

      formElement.addEventListener('submit', submitListener);

      return () => {
        formElement.removeEventListener('submit', submitListener);
      };
    }
  }, [getValues]);

  return (
    <GeneralContainer>
      {/* V4 FR-3.2 (audit Disc#9): en modo RV (hideExternalsTab) solo
          existe la pestaña Interno → renderizamos su contenido directo
          sin el selector de pestañas. En el resto de flujos (work-orders)
          se mantienen ambas pestañas. */}
      {hideExternalsTab ? (
        dataTabs[0].content
      ) : (
        <Tabs
          data={dataTabs}
          defaultIndex={selectedTab}
          typeTab={tabsType.WORK_ORDERS_ACTION}
          setSelectedIndexTab={setSelectedTab}
        />
      )}

      {/* V4 FR-3.2 (audit Disc#11): el RV no usa Distribució d'hores. */}
      {!hideHoursDistribution && (
        <>
      <StyledTypography component="h3" variant="semiBold" size="L">
        {t('operatorsAndVehicles.hoursDistribution')}
      </StyledTypography>
      <TimeContainer>
        <Controller
          control={control}
          name="registerInitDate"
          render={({ field: { onChange, value } }) => (
            <div data-testid="operators-date-start">
              <DateTime
                label={t('workOrder.startDate')}
                labelHour={t('workOrder.hour')}
                onChangeDate={onDateChange(onChange, value)}
                initialDate={defaultHoursData.registerInitDate}
                borderError={!!errors.registerInitDate}
                isDisabled={
                  initialData?.parentExpeditionOrderStatus ===
                  OrderState.Annulled ||
                  initialData?.parentExpeditionOrderStatus === OrderState.End ||
                  initialData?.state === OrderState.Annulled
                }
              />
            </div>
          )}
        />
        {/* Hora de llegada (opcional). No afecta al cálculo de horas. Va entre
            el inicio y el final porque es el orden real: salir, llegar al
            tajo y terminar (y la llegada tiene que ser anterior al final). */}
        <Controller
          control={control}
          name="registerArrivalDate"
          render={({ field: { onChange, value } }) => (
            <div data-testid="operators-date-arrival">
              <DateTime
                label={t('workOrder.arrivalDate')}
                labelHour={t('workOrder.hour')}
                // Igual que registerEndDate: la papelera debe dejar '' (no
                // undefined) para que el borrado sobreviva al sessionStorage.
                onChangeDate={onDateChange(onChange, value, '')}
                initialDate={defaultHoursData.registerArrivalDate}
                borderError={!!errors.registerArrivalDate}
                // Con texto bajo el campo, como «Datos Act» de la básica.
                error={errors.registerArrivalDate?.message}
                clearable
                isDisabled={
                  initialData?.parentExpeditionOrderStatus ===
                  OrderState.Annulled ||
                  initialData?.parentExpeditionOrderStatus === OrderState.End ||
                  initialData?.state === OrderState.Annulled
                }
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="registerEndDate"
          render={({ field: { onChange, value } }) => (
            <div data-testid="operators-date-end">
              <DateTime
                label={t('workOrder.endDate')}
                labelHour={t('workOrder.hour')}
                // La papelera llama onChangeDate() sin argumento; `undefined` en
                // RHF hace que JSON.stringify DESCARTE la clave al persistir a
                // sessionStorage y el borrado nunca llega al save. `''` = borrado
                // explícito (mismo valor que el estado inicial vacío).
                onChangeDate={onDateChange(onChange, value, '')}
                initialDate={defaultHoursData.registerEndDate}
                borderError={!!errors.registerEndDate}
                // Con texto bajo el campo, como «Datos Act» de la básica.
                error={errors.registerEndDate?.message}
                clearable
                isDisabled={
                  initialData?.parentExpeditionOrderStatus ===
                  OrderState.Annulled ||
                  initialData?.parentExpeditionOrderStatus === OrderState.End ||
                  initialData?.state === OrderState.Annulled
                }
              />
            </div>
          )}
        />

        {showHoursType && (
          <Controller
            control={control}
            name="hoursType"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options = translateHoursTypeOptions(
                defaultValues?.hoursTypeOptions as CatalogEntry[] | undefined,
                t
              );

              return (
                <DropDownInput
                  placeholder={t('operatorsAndVehicles.select')}
                  labelText={t('operatorsAndVehicles.hoursType')}
                  inputSize={'M'}
                  typeDropDown={'Default'}
                  options={options}
                  onChangeSelected={(option) => onChange(option?.value ?? '')}
                  defaultValue={defaultHoursData.hoursType}
                  borderError={!!errors.hoursType}
                  isDisabled={
                    initialData?.parentExpeditionOrderStatus ===
                    OrderState.Annulled ||
                    initialData?.parentExpeditionOrderStatus === OrderState.End ||
                    initialData?.state === OrderState.Annulled
                  }
                />
              );
            }}
          />
        )}
      </TimeContainer>

      {/* Desglose de horas-persona */}
      {(allOperators.length > 0 || (externsDataWatcher && externsDataWatcher.length > 0)) && (() => {
        const rawShiftMin = Math.abs(calculateHourDifference(finishTimeWatcher, startTimeWatcher));
        const fmtMin = (min: number) => {
          const h = Math.floor(min / 60);
          const m = min % 60;
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        };
        const intMin = allOperators.length * rawShiftMin;
        const extEntries = (externsDataWatcher || [])
          .filter((e: { operatorsNumber?: number | string }) => Number(e.operatorsNumber) > 0)
          .map((e: { company?: string; operatorsNumber?: number | string }) => ({
            name: e.company || '',
            count: Number(e.operatorsNumber),
            min: Number(e.operatorsNumber) * rawShiftMin,
          }));
        const extMin = extEntries.reduce((s: number, e: { min: number }) => s + e.min, 0);
        const total = intMin + extMin;
        const hasBoth = allOperators.length > 0 && extEntries.length > 0;

        return (
          <BreakdownContainer>
            {allOperators.length > 0 && (
              <InputText
                label={`${t('operatorsAndVehicles.internalOperatorsHours')} (${allOperators.length}) ${t('operatorsAndVehicles.personHours')}`}
                value={fmtMin(intMin)}
                disabled
                typeInput={'normal'}
                inputSize="S"
              />
            )}
            {extEntries.map((ext: { name: string; count: number; min: number }, i: number) => (
              <InputText
                key={i}
                label={`${ext.name} (${ext.count} op.) ${t('operatorsAndVehicles.personHours')}`}
                value={fmtMin(ext.min)}
                disabled
                typeInput={'normal'}
                inputSize="S"
              />
            ))}
            {hasBoth && (
              <>
                <InputText
                  label={t('operatorsAndVehicles.totalInternalHours')}
                  value={fmtMin(intMin)}
                  disabled
                  typeInput={'normal'}
                  inputSize="S"
                />
                <InputText
                  label={t('operatorsAndVehicles.totalExternalHours')}
                  value={fmtMin(extMin)}
                  disabled
                  typeInput={'normal'}
                  inputSize="S"
                />
              </>
            )}
            <InputText
              label={
                isSimultaneousWatcher
                  ? `${t('operatorsAndVehicles.totalPersonHours')} · ${t('operatorsAndVehicles.simultaneous.label')}`
                  : t('operatorsAndVehicles.totalPersonHours')
              }
              value={isSimultaneousWatcher ? '00:00' : fmtMin(total)}
              disabled
              typeInput={'normal'}
              inputSize="S"
            />
          </BreakdownContainer>
        );
      })()}
        </>
      )}

      {/* Switch: actuación simultánea — la franja se registra pero NO suma horas.
          Vive FUERA de {!hideHoursDistribution} porque en el flujo SR (P9 Crear
          Actuació desde RV) es cuando MÁS aplica: el operario patrulla y crea
          una Actuación cuya jornada ya se cuenta como parte del tram del RV.
          En cambio el wizard de CREACIÓN del RV (P3) lo oculta via
          hideSimultaneous (el RV no es una Actuación). */}
      {!hideSimultaneous && (
      <SimultaneousContainer>
        <SimultaneousHeader>
          <Typography size="S" variant="semiBold">
            {t('operatorsAndVehicles.simultaneous.label')}
          </Typography>
          <SimpleSwitch
            state={!!isSimultaneousWatcher}
            changeState={(next) => setValue('isSimultaneous', next)}
            disabled={
              initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
              initialData?.parentExpeditionOrderStatus === OrderState.End ||
              initialData?.state === OrderState.Annulled
            }
          />
        </SimultaneousHeader>
        <Typography size="XXS" variant="medium">
          {t('operatorsAndVehicles.simultaneous.hint')}
        </Typography>
      </SimultaneousContainer>
      )}

      {operatorError && (
        <ContainerError>
          <Typography size="XS" colorText="lightCoralRed">
            {operatorError}
          </Typography>
        </ContainerError>
      )}
      {/* Tour P5: errores externos (FR-3.4 del Iniciar del wizard RV) en el
          MISMO slot rojo de la card, en talla S para que se lean bien. */}
      {externalErrors && externalErrors.length > 0 && (
        <ContainerError>
          {externalErrors.map((msg) => (
            <Typography key={msg} size="S" colorText="lightCoralRed">
              {msg}
            </Typography>
          ))}
        </ContainerError>
      )}
      {!hideSubmitButton && (
      <ButtonContainer>
        <MainButton
          text={textButton}
          type="button" // ✅ Manual
          onClick={handleSaveDraft} // ✅ Guardar draft sin validar
          disabled={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
          }
        />
      </ButtonContainer>
      )}
    </GeneralContainer>
  );
};

export default OperatorsAndVehicleForm;
